import {
    useTranslate,
    useListContext,
    useNotify,
    useUnselectAll,
    useUpdateMany,
    Button,
    Confirm,
    useResourceContext,
    TextInput,
    maxLength,
    minLength,
    required,
} from 'react-admin';
import { bagStatuses } from '@/constants/Bag.jsx';
import SaveIcon from '@/assets/icons/save-icon.svg?react';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent.jsx';
import FormDialog from '@/components/global/drawers/FormDialog.jsx';
import ResendIcon from '@/assets/icons/resend-icon.svg?react';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { useState } from 'react';

export const ResendButton = () => {
    const { data, selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);

    const selectedData = data.filter(record => selectedIds.includes(record.id));
    const selectedStatuses = selectedData.map(record => record.status);
    const showButton =
        selectedIds.length > 0 && selectedStatuses.every(status => [bagStatuses.collected, bagStatuses.error, bagStatuses.manually_closed].includes(status));

    const [updateMany, { isLoading }] = useUpdateMany(
        resource,
        {
            ids: selectedIds,
            data: {
                ids: selectedIds,
                action: 'RESEND_TO_OPERATOR',
            },
        },
        {
            onSuccess: () => {
                notify(translate('resources.bag.labels.resendSuccess'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(translate('resources.bag.labels.resendFailed', { errorMessage: error.message }), { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        updateMany();
        setOpen(false);
    };

    return (
        showButton && (
            <>
                <Button label={translate('resources.bag.labels.resendToOperator')} onClick={handleClick}>
                    <ResendIcon />
                </Button>
                <Confirm
                    isOpen={open}
                    loading={isLoading}
                    title={translate('resources.bag.labels.resendToOperator')}
                    content={
                        <CustomConfirmContent
                            content={translate('resources.bag.labels.resendConfirm')}
                            handleDialogClose={handleDialogClose}
                            handleConfirm={handleConfirm}
                            loading={isLoading}
                        />
                    }
                    onConfirm={handleConfirm}
                    onClose={handleDialogClose}
                    sx={{ ...globalStyles.customConfirmDialog }}
                />
            </>
        )
    );
};
export const CloseManuallyButton = () => {
    const { data, selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);

    const selectedData = data.filter(record => selectedIds.includes(record.id));
    const selectedStatuses = selectedData.map(record => record.status);
    const showButton = selectedIds.length > 0 && selectedStatuses.every(status => ![bagStatuses.collected, bagStatuses.manually_closed].includes(status));
    const hasDeployed = selectedData.some(record => record.device?.deploymentStatus === 'deployed');
    const confirmMessage = hasDeployed
        ? translate('resources.bag.labels.closeManuallyDeployedConfirm')
        : translate('resources.bag.labels.closeManuallyConfirm');

    const [updateMany, { isLoading }] = useUpdateMany(
        resource,
        {
            ids: selectedIds,
            data: {
                ids: selectedIds,
                action: 'CLOSE_MANUALLY',
            },
        },
        {
            onSuccess: () => {
                notify(translate('resources.bag.labels.closeManuallySuccess'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(translate('resources.bag.labels.closeManuallyFailed', { errorMessage: error.message }), { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        updateMany();
        setOpen(false);
    };

    return (
        showButton && (
            <>
                <Button label={translate('resources.bag.labels.closeManually')} onClick={handleClick}>
                    <SaveIcon />
                </Button>
                <Confirm
                    isOpen={open}
                    loading={isLoading}
                    title={translate('resources.bag.labels.closeManually')}
                    content={
                        <CustomConfirmContent
                            content={confirmMessage}
                            handleDialogClose={handleDialogClose}
                            handleConfirm={handleConfirm}
                            loading={isLoading}
                        />
                    }
                    onConfirm={handleConfirm}
                    onClose={handleDialogClose}
                    sx={{ ...globalStyles.customConfirmDialog }}
                />
            </>
        )
    );
};

export const ChangeSealButton = () => {
    const { data, selectedIds } = useListContext();
    const translate = useTranslate();

    const selectedData = data.filter(record => selectedIds.includes(record.id));
    const selectedStatuses = selectedData.map(record => record.status);
    const showButton =
        selectedIds.length === 1 && selectedStatuses.every(status => [bagStatuses.collected, bagStatuses.manually_closed, bagStatuses.error].includes(status));

    const transform = formData => ({
        ...formData,
        action: 'CHANGE_SEAL',
    });

    return (
        showButton && (
            <>
                <FormDialog
                    key='FormDialog'
                    title={translate('resources.bag.labels.changeSeal')}
                    startIcon={<SaveIcon />}
                    transform={transform}
                    confirmTitle={translate('resources.bag.labels.changeSealConfirmTitle')}
                    confirmMessage={translate('resources.bag.labels.changeSealConfirm')}
                >
                    <TextInput
                        key='seal'
                        source='seal'
                        label={translate('resources.bag.fields.seal')}
                        fullWidth={true}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(30, translate('common.validation.maxLength', { count: 30 })),
                            minLength(3, translate('common.validation.minLength', { count: 3 })),
                        ]}
                    />
                </FormDialog>
            </>
        )
    );
};
